import React, { useEffect, useState } from 'react';

import {
  externalCalibrationService,
  pdfService,
  serialNumbersService,
} from 'services';

import './DocumentsOverview.scss';
import { ROUTES } from '../../constants/routes';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { CustomButton } from '../CustomButton';
import { Spinner } from '../Spinner';
import { ExternalCalibration } from '../../interfaces';
import { SerialNumber } from '../../interfaces/serialNumber';
import { AxiosResponse } from 'axios';

export const DocumentsOverview = () => {
  const { sn, certId } = useParams();
  const navigate = useNavigate();

  const [isAuthorizedPending, setIsAuthorizedPending] = useState<boolean>();
  const [isOriginalPending, setIsOriginalPending] = useState<boolean>(false);
  const [isWorksTestPending, setIsWorksTestPending] = useState<boolean>(false);
  const [isTAPending, setIsTAPending] = useState<boolean>(false);
  const [isDocPending, setIsDocPending] = useState<boolean>(false);
  const [isDnvPending, setIsDnvPending] = useState<boolean>(false);

  const [isCalError, setIsCalError] = useState<boolean>(false);
  const [isCalLoading, setIsCalLoading] = useState<boolean>(true);
  const [isSerialError, setIsSerialError] = useState<boolean>(false);

  const [isNoOriginalPdf, setIsNoOriginalPdf] = useState<boolean>(false);
  const [isNoAuthorizedPdf, setIsNoAuthorizedPdf] = useState<boolean>(false);
  const [isInvalidAuthorized, setIsInvalidAuthorized] =
    useState<boolean>(false);
  const [isNoWorksTestPdf, setIsNoWorksTestPdf] = useState<boolean>(false);
  const [isNoTAPdf, setIsNoTAPdf] = useState<boolean>(false);
  const [isNoDocPdf, setIsNoDocPdf] = useState<boolean>(false);
  const [isNoDnvPdf, setIsNoDnvPdf] = useState<boolean>(false);

  const [cal, setValues] = useState<ExternalCalibration>(
    {} as ExternalCalibration
  );
  const [serial, setSerial] = useState<SerialNumber>({} as SerialNumber);

  useEffect(() => {
    (async () => {
      try {
        if (certId) {
          let response;
          try {
            response = await externalCalibrationService.findByCertificateNumber(
              certId
            );
          } catch (e) {
            if (sn)
              response = await externalCalibrationService.findBySerialNumber(
                sn
              );
            else setIsCalError(true);
          }

          setValues(response);
        } else if (sn) {
          const response = await externalCalibrationService.findBySerialNumber(
            sn
          );

          setValues(response);
        }
      } catch (e) {
        setIsCalError(true);
      } finally {
        setIsCalLoading(false);
      }
    })();
  }, [sn, certId]);

  useEffect(() => {
    (async () => {
      try {
        if (sn) {
          const response = await serialNumbersService.findSerial(sn);
          setSerial(response);
          setIsNoOriginalPdf(!response.originalPdfAvailable);
          setIsInvalidAuthorized(!response.authorizedPdfValid);
          setIsNoAuthorizedPdf(!response.authorizedPdfAvailable);
          setIsNoWorksTestPdf(!response.worksTestPdfAvailable);
          setIsNoTAPdf(!response.typeApprovalPdfAvailable);
          setIsNoDnvPdf(!response.dnvPdfAvailable);
          setIsNoDocPdf(!response.docPdfAvailable);
        }
      } catch (e) {
        setIsSerialError(true);
      }
    })();
  }, [sn, certId]);

  const download = async () => {
    setIsAuthorizedPending(true);
    const response = await pdfService.authorizedCert(cal.certificateNr, cal.sn);
    triggerDownload(response, '_authorized.pdf');
    setIsAuthorizedPending(false);
  };

  const downloadOriginalCert = async () => {
    setIsOriginalPending(true);
    const response = await pdfService.originalCert(serial.sn);
    triggerDownload(response, '_original.pdf');
    setIsOriginalPending(false);
  };

  const downloadWorksTestCert = async () => {
    setIsWorksTestPending(true);
    const response = await pdfService.worksTestCert(serial.sn);
    triggerDownload(response, '_works_test.pdf');
    setIsWorksTestPending(false);
  };

  const downloadTACert = async () => {
    setIsTAPending(true);
    const response = await pdfService.typeApprovalCert(serial.sn);
    triggerDownload(response, '_type_approval.pdf');
    setIsTAPending(false);
  };

  const downloadDocCert = async () => {
    setIsDocPending(true);
    const response = await pdfService.docCert(serial.sn);
    triggerDownload(response, '_doc.pdf');
    setIsDocPending(false);
  };

  const downloadDnvCert = async () => {
    setIsDnvPending(true);
    const response = await pdfService.dnvCert(serial.sn);
    triggerDownload(response, '_dnv.pdf');
    setIsDnvPending(false);
  };

  function triggerDownload(response: AxiosResponse, suffix: string) {
    const fileURL = window.URL.createObjectURL(response as unknown as Blob);
    const alink = document.createElement('a');
    alink.href = fileURL;
    alink.download = serial.sn + suffix;
    alink.click();
  }

  return (
    <>
      {isSerialError && (
        <div className="documents-overview">
          <h1>Serial number: {sn}</h1>
          <div className="info">
            <h3>Serial number not found</h3>
            <div>
              An error occurred while trying to find the serial number.
              <br />
              <br />
              Please check the serial number and try again.
            </div>
          </div>
        </div>
      )}

      {!isSerialError && (
        <div className="documents-overview">
          <h1>Serial number: {serial.sn}</h1>
          {!isNoAuthorizedPdf && !isCalLoading && (
            <div className="calibration">
              {(isCalError ||
                isInvalidAuthorized ||
                !cal?.sn ||
                !cal?.certificateNr) && (
                <div className="info">
                  <h3>Authorized calibration check certificate</h3>
                  <div>
                    No calibration certificate found for this serial number.
                    <br />
                    <br />
                    Please note that authorized external calibration checks that
                    have been performed before the 1st January 2024 are not
                    available in the database! <br />
                    <br />
                    To obtain the latest calibration check certificate from a
                    date before the 1st January 2024, please contact our
                    authorized service partner accordingly.
                  </div>
                </div>
              )}
              {!isCalError &&
                !isInvalidAuthorized &&
                cal.sn &&
                cal.certificateNr && (
                  <div className="info">
                    <h3>Authorized calibration check certificate</h3>
                    <div>
                      Date of calibration:{' '}
                      {dayjs(cal.dateOfCalibration).format('DD MMMM, YYYY')}
                    </div>
                    <div>Place of calibration: {cal.placeOfCalibration}</div>
                    <div>Technician: {cal.technician}</div>
                  </div>
                )}

              {!isCalError &&
                !isInvalidAuthorized &&
                cal.sn &&
                cal.certificateNr && (
                  <div className="actions">
                    {
                      <CustomButton
                        text="Edit calibration"
                        onClick={() =>
                          navigate(
                            `${ROUTES.edit_authorized}/${cal.certificateNr}`
                          )
                        }
                      />
                    }

                    <div className="download-action">
                      <div className="spinner-box">
                        {isAuthorizedPending && <Spinner />}
                      </div>

                      <CustomButton
                        text="Download PDF"
                        onClick={() => download()}
                      />
                    </div>
                  </div>
                )}
            </div>
          )}

          {!isNoOriginalPdf && serial.sn && (
            <div className="calibration">
              <div className="info">
                <h3>Original calibration certificate</h3>
                <div>
                  Date of calibration:{' '}
                  {dayjs(serial.pruefdatum).format('DD MMMM, YYYY')}
                </div>
              </div>
              <div className="actions">
                {sn && (
                  <CustomButton
                    text="Edit calibration"
                    disabled={!serial.sn}
                    onClick={() =>
                      navigate(`${ROUTES.edit_original}/${serial.sn}`)
                    }
                  />
                )}

                <div className="download-action">
                  <div className="spinner-box">
                    {isOriginalPending && <Spinner />}
                  </div>

                  <CustomButton
                    text="Download PDF"
                    onClick={() => downloadOriginalCert()}
                  />
                </div>
              </div>
            </div>
          )}

          {!isNoWorksTestPdf && serial.sn && (
            <div className="calibration">
              <div className="info">
                <h3>Works test certificate</h3>
                <div>Alarm Test Point set to {serial.alarmpunkt}</div>
              </div>

              <div className="actions">
                <div className="download-action">
                  <div className="spinner-box">
                    {isWorksTestPending && <Spinner />}
                  </div>

                  <CustomButton
                    text="Download PDF"
                    onClick={() => downloadWorksTestCert()}
                  />
                </div>
              </div>
            </div>
          )}

          {!isNoTAPdf && serial.sn && (
            <div className="calibration">
              <div className="info">
                <h3>Type Approval</h3>

                <div>
                  Date of production:{' '}
                  {dayjs(serial.produktionsdatum).format('DD MMMM, YYYY')}
                </div>
                {/*{(isSerialError || isNoTAPdf || !serial.sn) && (*/}
                {/*  <div>*/}
                {/*    Your type approval is not available online.*/}
                {/*    <br /> <br /> Please contact{' '}*/}
                {/*    <a href="mailto:post@deckma.com">post@deckma.com</a>*/}
                {/*  </div>*/}
                {/*)}*/}
              </div>
              <div className="actions">
                <div className="download-action">
                  <div className="spinner-box">
                    {isTAPending && <Spinner />}
                  </div>

                  <CustomButton
                    text="Download PDF"
                    onClick={() => downloadTACert()}
                  />
                </div>
              </div>
            </div>
          )}

          {!isNoDnvPdf && serial.sn && (
            <div className="calibration">
              <div className="info">
                <h3>DNV Clean Design</h3>
                <div>Alarm Test Point set to {serial.alarmpunkt}</div>
              </div>
              <div className="actions">
                <div className="download-action">
                  <div className="spinner-box">
                    {isDnvPending && <Spinner />}
                  </div>

                  <CustomButton
                    text="Download PDF"
                    onClick={() => downloadDnvCert()}
                  />
                </div>
              </div>
            </div>
          )}
          {!isNoDocPdf && serial.sn && (
            <div className="calibration">
              <div className="info">
                <h3>Declaration of Conformity</h3>
                <div>
                  Date of production:{' '}
                  {dayjs(serial.produktionsdatum).format('DD MMMM, YYYY')}
                </div>
              </div>
              <div className="actions">
                <div className="download-action">
                  <div className="spinner-box">
                    {isDocPending && <Spinner />}
                  </div>

                  <CustomButton
                    text="Download PDF"
                    onClick={() => downloadDocCert()}
                  />
                </div>
              </div>
            </div>
          )}
          <br />
          <br />
        </div>
      )}
    </>
  );
};
